import {LayerSpecification, StyleSpecification} from 'maplibre-gl'

export type MapProvider = 'OFM' | 'OSM' | 'HERE' | 'PTV' | 'WEBFLEET' | 'GOOGLE'

/** Default map center in MapLibre order [lng, lat]. */
export const DEFAULT_CENTER: [number, number] = [18.0220, 53.1150]

// OpenFreeMap is the default base map: a free, key-less MapLibre VECTOR style (not XYZ raster).
// Public instance by default; override with VUE_APP_OPENFREEMAP_STYLE_URL to self-host (recommended for heavy/commercial traffic).
const OPENFREEMAP_STYLE_URL = process.env.VUE_APP_OPENFREEMAP_STYLE_URL ?? 'https://tiles.openfreemap.org/styles/positron'

/**
 * Maps the raw MAP_TYPE token parameter to a supported MapLibre provider.
 * Any unknown value falls back to OFM (OpenFreeMap, the default base map); the
 * legacy raster OSM stays reachable via an explicit MAP_TYPE=OSM.
 */
export function resolveProvider(mapType: string | null | undefined): MapProvider {
  switch (mapType) {
    case 'OSM':
      return 'OSM'
    case 'HERE':
      return 'HERE'
    case 'PTV':
      return 'PTV'
    case 'WEBFLEET':
      return 'WEBFLEET'
    case 'GOOGLE':
      return 'GOOGLE'
    default:
      return 'OFM'
  }
}

/** Raster tile configuration for a single base-map provider. */
interface RasterTiles {
  tiles: string[]
  tileSize: number
  attribution: string
}

/** Wraps a raster tile configuration into a MapLibre style. */
function buildRasterStyle(config: RasterTiles): StyleSpecification {
  return {
    version: 8,
    sources: {
      basemap: {type: 'raster', tiles: config.tiles, tileSize: config.tileSize, attribution: config.attribution}
    },
    layers: [{id: 'basemap', type: 'raster', source: 'basemap'}]
  }
}

/**
 * Whether a base-map provider is usable, i.e. its API key (if any) is configured. HERE/PTV/
 * WebFleet/Google need a key — without it the tile server returns 401 and the map is blank, so
 * such providers should not be offered in the switcher. OFM/OSM are key-less and always available.
 */
export function isProviderAvailable(provider: MapProvider): boolean {
  switch (provider) {
    case 'HERE':
      return !!process.env.VUE_APP_HERE_MAP_API_KEY
    case 'PTV':
      return !!process.env.VUE_APP_PTV_API_KEY
    case 'WEBFLEET':
      return !!process.env.VUE_APP_WEBFLEET_API_KEY
    case 'GOOGLE':
      return !!process.env.VUE_APP_GOOGLE_MAP_API_KEY
    default:
      return true
  }
}

function providerTiles(provider: MapProvider): RasterTiles {
  const year = new Date().getFullYear()

  switch (provider) {
    case 'HERE':
      return {
        tiles: [
          `https://2.base.maps.ls.hereapi.com/maptile/2.1/maptile/newest/normal.day/{z}/{x}/{y}/512/png8?apiKey=${process.env.VUE_APP_HERE_MAP_API_KEY}&ppi=320`
        ],
        tileSize: 512,
        attribution: `&copy; Here, ${year} | Digitrack 2.0`
      }

    case 'PTV':
      // PTV Developer Raster Maps API. NOTE: verify endpoint/style params against current PTV docs.
      return {
        tiles: [
          `https://api.myptv.com/rastermaps/v1/image/{z}/{x}/{y}.png?apiKey=${process.env.VUE_APP_PTV_API_KEY}`
        ],
        tileSize: 256,
        attribution: `&copy; PTV, ${year} | Digitrack 2.0`
      }

    case 'WEBFLEET':
      // WebFleet base map is served by TomTom's Raster Tile API.
      return {
        tiles: [
          `https://api.tomtom.com/map/1/tile/basic/main/{z}/{x}/{y}.png?key=${process.env.VUE_APP_WEBFLEET_API_KEY}`
        ],
        tileSize: 256,
        attribution: `&copy; TomTom, ${year} | Digitrack 2.0`
      }

    default:
      return {
        tiles: [
          'https://a.tile.openstreetmap.org/{z}/{x}/{y}.png',
          'https://b.tile.openstreetmap.org/{z}/{x}/{y}.png',
          'https://c.tile.openstreetmap.org/{z}/{x}/{y}.png'
        ],
        tileSize: 256,
        attribution: `&copy; OpenStreetMap, ${year} | Digitrack 2.0`
      }
  }
}

/**
 * Synchronous raster style for providers exposing plain XYZ tiles (OSM/HERE/PTV/WEBFLEET).
 * GOOGLE has no static XYZ tiles and degrades to OSM here — use createMapStyle for real Google.
 */
export function createRasterStyle(provider: MapProvider): StyleSpecification {
  return buildRasterStyle(providerTiles(provider))
}

/**
 * Google Map Tiles API needs a session token created up front, so its style is async.
 * Falls back to OSM when the key is missing or the Map Tiles API is not enabled (non-2xx).
 */
async function createGoogleRasterStyle(): Promise<StyleSpecification> {
  const key = process.env.VUE_APP_GOOGLE_MAP_API_KEY

  try {
    const response = await fetch(`https://tile.googleapis.com/v1/createSession?key=${key}`, {
      method: 'POST',
      headers: {'Content-Type': 'application/json'},
      body: JSON.stringify({mapType: 'roadmap', language: 'pl-PL', region: 'PL'})
    })

    if (!response.ok) {
      return createRasterStyle('OSM')
    }

    const {session} = await response.json()

    return buildRasterStyle({
      tiles: [`https://tile.googleapis.com/v1/2dtiles/{z}/{x}/{y}?session=${session}&key=${key}`],
      tileSize: 256,
      attribution: `&copy; Google, ${new Date().getFullYear()} | Digitrack 2.0`
    })
  } catch {
    return createRasterStyle('OSM')
  }
}

// TomTom Orbis vector style (bundled in public/); {mapKey} placeholders are filled from env at runtime.
const TOMTOM_VECTOR_STYLE_URL = 'map-styles/tomtom-orbis-light.json'
// Keep only the base street map; drop traffic/satellite/hillshade (extra tile calls, unwanted on a fleet base map).
const TOMTOM_KEEP_SOURCES = ['vectorTiles']

/** Strips everything but the base street layer/source from a TomTom Orbis style. */
function trimTomTomStyle(style: StyleSpecification): StyleSpecification {
  if (Array.isArray(style.layers)) {
    style.layers = style.layers.filter(
      (layer) => !('source' in layer) || TOMTOM_KEEP_SOURCES.includes((layer as {source?: string}).source ?? '')
    )
  }

  if (style.sources) {
    const sources = style.sources as Record<string, unknown>
    Object.keys(sources).forEach((id) => {
      if (!TOMTOM_KEEP_SOURCES.includes(id)) {
        delete sources[id]
      }
    })
  }

  return style
}

/**
 * WebFleet base map = TomTom Orbis vector style for richer detail/labels (mirrors digitrack v1).
 * Falls back to the TomTom raster tiles if the style cannot be loaded.
 */
async function createTomTomVectorStyle(): Promise<StyleSpecification> {
  const key = process.env.VUE_APP_WEBFLEET_API_KEY ?? ''

  try {
    const response = await fetch(`${process.env.BASE_URL ?? '/'}${TOMTOM_VECTOR_STYLE_URL}`)
    if (!response.ok) {
      return createRasterStyle('WEBFLEET')
    }

    const style = JSON.parse((await response.text()).replace(/\{mapKey\}/g, key)) as StyleSpecification
    const trimmed = trimTomTomStyle(style)

    // Keep the branded attribution format consistent with the other (raster) providers.
    const base = trimmed.sources?.vectorTiles as {attribution?: string} | undefined
    if (base) {
      base.attribution = `&copy; TomTom, ${new Date().getFullYear()} | Digitrack 2.0`
    }

    return trimmed
  } catch {
    return createRasterStyle('WEBFLEET')
  }
}

// Vector layers positron strips out, re-added from the OpenFreeMap "liberty" style (same OpenMapTiles
// source + fonts). 3D buildings: verbatim from liberty, visible once the camera is pitched (zoom >= 14).
const OFM_BUILDINGS_3D_LAYER: LayerSpecification = {
  id: 'building-3d',
  type: 'fill-extrusion',
  source: 'openmaptiles',
  'source-layer': 'building',
  minzoom: 17,
  paint: {
    'fill-extrusion-base': ['get', 'render_min_height'],
    'fill-extrusion-color': 'hsl(35,8%,85%)',
    'fill-extrusion-height': ['get', 'render_height'],
    'fill-extrusion-opacity': 0.8
  }
}

// POI labels only (no icons) so important places are named without cluttering positron's minimal look.
// Uses the same `poi` source-layer and font as liberty; the rank filter trims minor POIs.
const OFM_POI_LABELS_LAYER: LayerSpecification = {
  id: 'poi-labels',
  type: 'symbol',
  source: 'openmaptiles',
  'source-layer': 'poi',
  minzoom: 16,
  filter: ['all',
    ['match', ['geometry-type'], ['Point', 'MultiPoint'], true, false],
    ['<=', ['get', 'rank'], 12]
  ],
  layout: {
    'text-field': ['coalesce', ['get', 'name_en'], ['get', 'name']],
    'text-font': ['Noto Sans Regular'],
    'text-anchor': 'top',
    'text-offset': [0, 0.6],
    'text-max-width': 9,
    'text-size': 12
  },
  paint: {
    'text-color': '#666',
    'text-halo-color': '#ffffff',
    'text-halo-width': 1,
    'text-halo-blur': 0.5
  }
}

/**
 * Re-adds the vector layers positron omits, when its OpenMapTiles source is present: 3D buildings beneath
 * the first text-label layer (so place/road names stay readable) and POI labels on top of everything.
 */
function withExtraVectorLayers(style: StyleSpecification): StyleSpecification {
  if (!style.sources || !('openmaptiles' in style.sources) || !Array.isArray(style.layers)) {
    return style
  }
  const layers = style.layers
  const firstSymbol = layers.findIndex((layer) => layer.type === 'symbol')
  const insertAt = firstSymbol === -1 ? layers.length : firstSymbol
  style.layers = [
    ...layers.slice(0, insertAt),
    OFM_BUILDINGS_3D_LAYER,
    ...layers.slice(insertAt),
    OFM_POI_LABELS_LAYER
  ]
  return style
}

// MapLibre renders labels from SDF glyph PBFs, not CSS fonts. OpenFreeMap hosts only Noto Sans, so we ship
// the project's main font (Poppins, the Metronic `--bs-font-sans-serif`) as glyphs under public/fonts and
// repoint the style to them. Only Regular + Bold are hosted (ranges 0-511 = Latin + Polish).
const PROJECT_GLYPHS_URL = `${process.env.BASE_URL ?? '/'}fonts/{fontstack}/{range}.pbf`

/** Maps an OpenFreeMap (Noto Sans) font stack to the hosted Poppins faces (Regular/Bold; italic -> regular). */
function toProjectFontStack(stack: string[]): string[] {
  return stack.map((face) => (face.includes('Bold') ? 'Poppins Bold' : 'Poppins Regular'))
}

// Poppins glyphs are hosted only for the Latin ranges (block start < 512). Base-map labels in
// other scripts (Greek, Cyrillic, …) would 404 against public/fonts and flood the console, so
// redirect those higher ranges to OpenFreeMap's Noto Sans (full Unicode). Latin/Polish stay
// Poppins; if the fallback host is unreachable MapLibre still renders those codepoints locally.
const OPENFREEMAP_FONTS_BASE = 'https://tiles.openfreemap.org/fonts'
const HOSTED_GLYPH_RANGE_LIMIT = 512

/** MapLibre transformRequest: reroutes not-hosted Poppins glyph ranges to OpenFreeMap Noto Sans. */
export function transformMapRequest(url: string): { url: string } | undefined {
  const match = /\/fonts\/([^/]+)\/(\d+)-(\d+)\.pbf/.exec(url)
  if (!match || Number(match[2]) < HOSTED_GLYPH_RANGE_LIMIT) {
    return undefined
  }
  const face = decodeURIComponent(match[1]).includes('Bold') ? 'Noto Sans Bold' : 'Noto Sans Regular'
  return { url: `${OPENFREEMAP_FONTS_BASE}/${encodeURIComponent(face)}/${match[2]}-${match[3]}.pbf` }
}

/** Repoints the style's glyphs + every literal text-font to the project's Poppins; expressions are left as-is. */
function withProjectFont(style: StyleSpecification): StyleSpecification {
  style.glyphs = PROJECT_GLYPHS_URL
  if (Array.isArray(style.layers)) {
    for (const layer of style.layers) {
      const layout = (layer as {layout?: Record<string, unknown>}).layout
      const stack = layout?.['text-font']
      if (Array.isArray(stack) && stack.every((face) => typeof face === 'string')) {
        layout!['text-font'] = toProjectFontStack(stack as string[])
      }
    }
  }
  return style
}

/**
 * OpenFreeMap vector style (the default base map): a free, key-less hosted MapLibre style.
 * Fetches the style JSON; falls back to the raster OSM tiles if it cannot be loaded.
 * The style ships its own attribution (OpenFreeMap / OpenMapTiles / OpenStreetMap) — kept as the licence requires.
 */
async function createOpenFreeMapStyle(): Promise<StyleSpecification> {
  try {
    const response = await fetch(OPENFREEMAP_STYLE_URL)
    if (!response.ok) {
      return createRasterStyle('OSM')
    }

    return withProjectFont(withExtraVectorLayers((await response.json()) as StyleSpecification))
  } catch {
    return createRasterStyle('OSM')
  }
}

/** Resolves a MapLibre style for the provider; async (OFM/WEBFLEET fetch a vector style, GOOGLE needs a session token). */
export async function createMapStyle(provider: MapProvider): Promise<StyleSpecification> {
  if (provider === 'OFM') {
    return createOpenFreeMapStyle()
  }

  if (provider === 'GOOGLE') {
    return createGoogleRasterStyle()
  }

  if (provider === 'WEBFLEET') {
    return createTomTomVectorStyle()
  }

  return createRasterStyle(provider)
}
